
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Send, Mic, Pause, Loader2 } from "lucide-react";
import { FormTextarea } from "@/components/ui/form-textarea";
import { useVoiceRecording } from "@/hooks/useVoiceRecording";

interface MessageInputProps {
  onSendMessage: (message: string) => void;
  isLoading: boolean;
}

const MessageInput: React.FC<MessageInputProps> = ({ onSendMessage, isLoading }) => {
  const [message, setMessage] = useState("");
  const { isRecording, isProcessing, startRecording, stopRecording, cancelRecording } = useVoiceRecording();

  const handleSendMessage = () => {
    if (!message.trim()) return;
    onSendMessage(message);
    setMessage("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleVoiceRecord = async () => {
    if (isRecording) {
      try {
        const transcribedText = await stopRecording();
        if (transcribedText.trim()) {
          setMessage(prev => prev ? `${prev} ${transcribedText}` : transcribedText);
        }
      } catch (error) {
        console.error('Recording failed:', error);
      }
    } else {
      startRecording();
    }
  };

  return (
    <div className="flex items-start gap-2 p-4 w-full max-w-full">
      <div className="flex-grow w-full max-w-[calc(100%-120px)]">
        <FormTextarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={isRecording ? "Listening..." : "Share your faith with love and truth..."}
          className="w-full"
          disabled={isLoading || isProcessing}
        />
      </div>
      
      <Button 
        onClick={handleVoiceRecord}
        disabled={isLoading || isProcessing}
        variant={isRecording ? "destructive" : "outline"}
        className="shrink-0"
        title={isRecording ? "Stop recording" : "Start voice recording"}
      >
        {isProcessing ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : isRecording ? (
          <Pause className="h-5 w-5" />
        ) : (
          <Mic className="h-5 w-5" />
        )}
      </Button>
      
      <Button 
        onClick={handleSendMessage} 
        disabled={!message.trim() || isLoading || isRecording || isProcessing}
        className="bg-heaven-gold hover:bg-heaven-dark-gold shrink-0"
      >
        <Send className="h-5 w-5" />
      </Button>
    </div>
  );
};

export default MessageInput;
